import {IAnimation} from '@fantasmagames/core/src/display/_interfaces/IAnimation';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {Updatable} from '@fantasmagames/core/src/update/Updatable';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';

export class SpineAnimation extends Updatable implements IAnimation {
	protected _spine: Spine;
	protected _name: string;
	protected _loop: boolean;
	protected _dontDestroyOnComplete: boolean;
	protected _completeCallback: Function;
	protected _completed: boolean;
	protected _chainedIdleAnimation: string;
	protected _chainedIdle: boolean;

	protected _loopComplete: boolean = false;
	protected _loopCount: number = 0;
	protected _loopLimit: number = -1;

	constructor(spine: Spine, name: string, loop?: boolean, dontDestroyOnComplete?: boolean) {
		super();

		this._spine = spine;
		this._name = name;
		this._loop = loop;
		this._dontDestroyOnComplete = dontDestroyOnComplete;
	}

	public setOnComplete(callback: Function): void {
		this._completeCallback = callback;
	}

	public setLoopLimit(loopLimit: number): void {
		this._loopLimit = loopLimit;
	}

	public start(): void {
		super.start();

		if (!__PRODUCTION__) {
			this.debugcheckHasAnimation(this._name);
		}

		this._spine.state.setAnimation(0, this._name, this._loop);

		if (!this._loop) {
			this._spine.state.addListener({
				complete: () => {
					// we cannot destroy spine here because we're in the middle of Spine::update()
					this._completed = true;
				},
			});
		} else {
			if (this._loopLimit > 0) {
				this._spine.state.addListener({
					complete: () => {
						this._loopCount++;
						if (this._loopCount === this._loopLimit) {
							this._loopLimit = -1;
							this._completed = true;
						}
					},
				});
			}
		}

		// initial state
		this.update(0);
	}

	public stop(): void {
		super.stop();

		this.unChainIdleAnimation();

		if (!this._dontDestroyOnComplete) {
			if (this._spine && this._spine.skeleton) {
				this._spine.destroy();
			}
		}
	}

	public forceDestroy(): void {
		this.stop();
		if (this._spine && this._spine.skeleton) {
			this._spine.destroy();
		}
	}

	public hasAnimationWithName(name: string): boolean {
		return this._spine.state.hasAnimation(name);
	}

	public update(dt: number): void {
		this._spine.update(dt);

		if (this._completed) {
			this._completed = false;
			this._completeCallback && this._completeCallback();
			if (this._chainedIdle && this.chainIdleAnimation) {
				this._completed = false;
				this.changeAnimationTo(this._chainedIdleAnimation, true);
				this.start();
			} else {
				this.stop();
			}
		}
	}

	public changeAnimationTo(name: string, loop: boolean, loopLimit: number = -1): void {
		this._name = name;
		this._loop = loop;
		this._loopCount = 0;
		this._loopLimit = loopLimit;

		if (!__PRODUCTION__) {
			this.debugcheckHasAnimation(name);
		}

		this._spine.state.setAnimation(0, name, loop);

		if (!this._loop) {
			this._spine.state.addListener({
				complete: () => {
					// we cannot destroy spine here because we're in the middle of Spine::update()
					this._completed = true;
				},
			});
		} else {
			this._spine.state.clearListeners();

			if (this._loopLimit > 0) {
				this._spine.state.addListener({
					complete: () => {
						this._loopCount++;
						if (this._loopCount === this._loopLimit) {
							this._loopLimit = -1;
							this._completed = true;
						}
					},
				});
			}
		}

		// initial state
		this.update(0);
	}

	public chainIdleAnimation(name: string): void {
		this._chainedIdle = true;
		this._chainedIdleAnimation = name;
	}

	public unChainIdleAnimation(): void {
		this._chainedIdle = false;
		this._chainedIdleAnimation = null;
	}

	public gotoRandomAnimationPosition(): void {
		this._spine.state.getCurrent(0).trackTime = GameUtils.randomInt(0, this._spine.state.getCurrent(0).animationEnd);
	}

	public gotoAnimationFrame(frame: number, trackIndex: number = 0): void {
		this._spine.state.getCurrent(trackIndex).trackTime = frame / 60;
	}

	public set timeScale(value: number) {
		(this.container as Spine).state.timeScale = value;
	}

	public get timeScale(): number {
		return (this.container as Spine).state.timeScale;
	}

	public get container(): PIXI.Container {
		return this._spine;
	}

	public get name(): string {
		return this._name;
	}

	protected debugcheckHasAnimation(animationName: string): boolean {
		const hasAnimation = !!this._spine.stateData.skeletonData.findAnimation(animationName);
		if (!hasAnimation) {
			console.warn(`Animation ${animationName} not found on: ${this._spine.stateData.skeletonData.name}`);
		}

		return hasAnimation;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/display/SpineAnimation.ts